import React from 'react';
import { VersionComparison as VersionComparisonType, QuestionDiff } from '../services/versionService';

interface VersionComparisonProps {
  comparison: VersionComparisonType;
  onClose: () => void;
}

const VersionComparison: React.FC<VersionComparisonProps> = ({ comparison, onClose }) => {
  const getDiffColor = (diffType: QuestionDiff['diffType']) => {
    switch (diffType) {
      case 'ADDED':
        return 'bg-green-50 border-green-200';
      case 'DELETED':
        return 'bg-red-50 border-red-200';
      case 'MODIFIED':
        return 'bg-yellow-50 border-yellow-200';
      default:
        return 'bg-gray-50 border-gray-200';
    }
  };

  const getDiffBadge = (diffType: QuestionDiff['diffType']) => {
    switch (diffType) {
      case 'ADDED':
        return <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-medium">新增</span>;
      case 'DELETED':
        return <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs font-medium">删除</span>;
      case 'MODIFIED':
        return <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded text-xs font-medium">修改</span>;
      default:
        return <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs font-medium">未变化</span>;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">版本对比</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Version Info */}
          <div className="mt-4 grid grid-cols-2 gap-4">
            <div className="p-3 bg-blue-50 rounded-lg">
              <div className="text-xs text-gray-600 mb-1">旧版本</div>
              <div className="font-medium text-gray-900">{comparison.oldVersion.title}</div>
              <div className="text-sm text-gray-600">{comparison.oldVersion.version}</div>
            </div>
            <div className="p-3 bg-purple-50 rounded-lg">
              <div className="text-xs text-gray-600 mb-1">新版本</div>
              <div className="font-medium text-gray-900">{comparison.newVersion.title}</div>
              <div className="text-sm text-gray-600">{comparison.newVersion.version}</div>
            </div>
          </div>

          {/* Stats */}
          <div className="mt-4 flex gap-4 text-sm">
            <div className="flex items-center gap-2">
              <span className="px-2 py-1 bg-green-100 text-green-700 rounded">
                新增 {comparison.addedCount}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="px-2 py-1 bg-red-100 text-red-700 rounded">
                删除 {comparison.deletedCount}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded">
                修改 {comparison.modifiedCount}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded">
                未变化 {comparison.unchangedCount}
              </span>
            </div>
          </div>
        </div>

        {/* Diff List */}
        <div className="flex-1 overflow-y-auto p-6">
          {comparison.questionDiffs.filter(diff => diff.diffType !== 'UNCHANGED').length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-600">两个版本的问题完全相同，没有差异</p>
            </div>
          ) : (
          <div className="space-y-3">
            {comparison.questionDiffs
              .filter(diff => diff.diffType !== 'UNCHANGED')
              .map((diff, index) => (
                <div
                  key={index}
                  className={`p-4 rounded-lg border ${getDiffColor(diff.diffType)}`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      {diff.diffType === 'DELETED' && diff.oldQuestion && (
                        <div>
                          <h3 className="font-medium text-gray-900">{diff.oldQuestion.title}</h3>
                          <p className="text-sm text-gray-600 mt-1">{diff.oldQuestion.typeDisplayName}</p>
                        </div>
                      )}
                      {diff.diffType === 'ADDED' && diff.newQuestion && (
                        <div>
                          <h3 className="font-medium text-gray-900">{diff.newQuestion.title}</h3>
                          <p className="text-sm text-gray-600 mt-1">{diff.newQuestion.typeDisplayName}</p>
                        </div>
                      )}
                      {diff.diffType === 'MODIFIED' && (
                        <div>
                          <h3 className="font-medium text-gray-900">{diff.newQuestion?.title || diff.oldQuestion?.title}</h3>
                          <p className="text-sm text-gray-600 mt-1">{diff.newQuestion?.typeDisplayName || diff.oldQuestion?.typeDisplayName}</p>
                        </div>
                      )}
                    </div>
                    {getDiffBadge(diff.diffType)}
                  </div>

                  {/* Changes */}
                  {diff.changes && diff.changes.length > 0 && (
                    <div className="mt-3 space-y-1">
                      {diff.changes.map((change, idx) => (
                        <div key={idx} className="text-sm text-gray-700 flex items-start gap-2">
                          <span className="text-gray-400">•</span>
                          <span>{change}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
          </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
          >
            关闭
          </button>
        </div>
      </div>
    </div>
  );
};

export default VersionComparison;
